import { useState } from 'react';

/**
 * Generic hook for form draft persistence using localStorage.
 *
 * @param {string|null} storageKey - Unique key for localStorage. Pass null to disable (e.g. edit mode).
 * @returns {{ draftData, saveDraft, clearDraft, hasDraft, lastSaved, draftActiveTab }}
 */
const useFormDraft = (storageKey) => {
	// Lazy initialization — reads localStorage only once on mount
	const [draftState, setDraftState] = useState(() => {
		if (!storageKey) return null;
		try {
			const stored = localStorage.getItem(storageKey);
			if (stored) return JSON.parse(stored);
		} catch {
			// Corrupt or unreadable storage — start clean
		}
		return null;
	});

	/**
	 * Persists the current form state as a draft.
	 * @param {object} data - Form data to persist (formValues + array lists).
	 * @param {number} activeTab - Current active tab index.
	 */
	const saveDraft = (data, activeTab) => {
		if (!storageKey) return;
		const draft = {
			data,
			metadata: {
				lastSaved: new Date().toISOString(),
				activeTab,
			},
		};
		try {
			localStorage.setItem(storageKey, JSON.stringify(draft));
			setDraftState(draft);
		} catch (error) {
			console.error('[useFormDraft] Error saving draft:', error);
		}
	};

	/**
	 * Removes the draft from localStorage and resets internal state.
	 */
	const clearDraft = () => {
		if (!storageKey) return;
		try {
			localStorage.removeItem(storageKey);
		} catch {
			// ignore
		}
		setDraftState(null);
	};

	return {
		draftData: draftState?.data ?? null,
		saveDraft,
		clearDraft,
		hasDraft: draftState !== null,
		lastSaved: draftState?.metadata?.lastSaved ?? null,
		draftActiveTab: draftState?.metadata?.activeTab ?? null,
	};
};

export default useFormDraft;
